import { UITable } from '../../components/table/ui-table.js';
import { debounce } from '../../global/helpers.js';
import { browser } from '../../global/index.js';
import { getBaseGridPoint } from '../../global/ui-helpers.js';

/**
 * @memberof SharedComponents
 * @classdesc Class for ui-table editable plugin.
 * @alias UITableEditable
 * @implements {ComponentPlugin}
 * @param {HTMLTableElement} target Target instance.
 */
class UITableEditable {
    constructor(target) {
        this.target = target;
    }

    /**
     * @type {NodeListOf<HTMLTableCellElement>}
     */
    get cells() {
        return this.target.querySelectorAll('td');
    }

    /**
     * @type {number}
     */
    static get WidthOffset() {
        return getBaseGridPoint() * 3;
    }

    /**
     * Check if table is remove cell for cloneable plugin.
     * @param {HTMLTableCellElement} td
     * @returns {boolean}
     * @private
     */
    isRemoveCell(td) {
        return td.classList.contains('-remove');
    }

    /**
     * Check if table data cell contains non elements nodes.
     * @param {HTMLTableCellElement} td
     * @returns {boolean}
     * @private
     */
    hasOnlyTextableNodes(td) {
        return (
            [].filter.call(
                td.childNodes,
                (child) => child.nodeType === Node.ELEMENT_NODE
            ).length === 0
        );
    }

    /**
     * Fixes the cells widths.
     * @private
     */
    fixateCells() {
        this.cells.forEach((cell) => {
            if (browser.mobileMediaMatches()) {
                cell.style.width = '';
            } else {
                const width =
                    cell.getBoundingClientRect().width -
                    this.constructor.WidthOffset;
                cell.style.width = `${width}px`;
            }
        });
    }

    /**
     * @private
     */
    subscribeEvents() {
        browser.mobileMedia.addEventListener('change', this.cellFixator);
        browser.tabletMedia.addEventListener('change', this.cellFixator);
        browser.desktopMedia.addEventListener('change', this.cellFixator);
    }
    /**
     * @private
     */
    unsubscribeEvents() {
        browser.mobileMedia.removeEventListener('change', this.cellFixator);
        browser.tabletMedia.removeEventListener('change', this.cellFixator);
        browser.desktopMedia.removeEventListener('change', this.cellFixator);
    }

    /**
     * @inheritDoc
     */
    render() {
        this.target.table.classList.add('-editable');
        const rows = this.target.querySelectorAll('tbody tr');
        if (rows.length > 0) {
            [].forEach.call(
                this.target.querySelectorAll('tbody td'),
                (cell) => {
                    cell.classList.add('col');
                    if (
                        this.isRemoveCell(cell) ||
                        this.hasOnlyTextableNodes(cell)
                    ) {
                        return;
                    }
                    cell.classList.add('-valign-top');
                }
            );
        }
        this.fixateCells();
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.cellFixator = debounce(this.fixateCells.bind(this), 100);
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.subscribeEvents();
    }
}

UITable.registerPlugin('editable', UITableEditable);

export { UITableEditable };
